import {
    ApolloClient,
    InMemoryCache,
    ApolloLink,
    HttpLink,
    from,
    Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { getTimezone } from "@/utils/timezone";

import { REFRESH_TOKEN } from "./mutations/auth";
import { localStorageWrapper } from "@/utils/storage";
import { unsetSupportId } from "@/utils/support";

const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL;
const paymentBaseUrl = process.env.NEXT_PUBLIC_PAYMENT_API_URL;

const PAYMENT_OPERATIONS = ["payDonate", "payDeposit", "payFine"];

const dynamicLink = new ApolloLink((operation, forward) => {
    let uri = apiBaseUrl;

    if (PAYMENT_OPERATIONS.indexOf(operation?.operationName) > -1) {
        uri = paymentBaseUrl;
    }

    // Set the URI dynamically
    const httpLink = new HttpLink({ uri });

    // Forward the operation to the dynamically selected HttpLink
    return httpLink.request(operation, forward);
});

const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
        headers:
            [
                "token",
                "tokenRefresh",
                "otpStepOne",
                "otpStepTwo",
                "examReg",
                "lessonPreReg",
                "registerTrackerCreate",
                "registerTrackerUpdate",
                "registrationTracker",
                "usersCount",
            ].indexOf(operation.operationName) > -1
                ? { ...headers, authorization: null }
                : {
                      ...headers,
                      authorization:
                          `jwt ${localStorageWrapper.getToken()}` || null,
                  },
    }));

    return forward(operation);
});

const dateTimeHeaderMiddleware = new ApolloLink((operation, forward) => {
    // add the authorization to the headers
    const timezone = getTimezone();

    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            "X-Timezone": timezone,
        },
    }));

    return forward(operation);
});

const impersonatedHeaderMiddleware = new ApolloLink((operation, forward) => {
    // add the authorization to the headers
    const isImpersonated = localStorageWrapper.isImpersonated();

    if (isImpersonated) {
        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                "X-impersonated": isImpersonated,
            },
        }));
    }

    return forward(operation);
});

const ERROR_MESSAGES = [
    "Refresh token is expired",
    "Invalid refresh token",
    "Error decoding signature",
    "User is disabled",
];

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
    refreshSubscribers.push(cb);
};

const onRefreshComplete = (token) => {
    refreshSubscribers.map((cb) => cb(token));
    refreshSubscribers = [];
};

export const getNewToken = async () => {
    try {
        const refreshToken = localStorageWrapper.getRefreshToken();
        if (!refreshToken) {
            forceLogout("No refresh token available");
            return null;
        }

        const result = await client.mutate({
            mutation: REFRESH_TOKEN,
            variables: { refreshToken: refreshToken },
        });

        const newToken = result?.data?.tokenRefresh?.token;
        const newRefreshToken = result?.data?.tokenRefresh?.refreshToken;

        if (!newToken || !newRefreshToken) {
            forceLogout("Failed to refresh token");
            return null;
        }

        localStorageWrapper
            .clearAuthValues()
            .setToken(newToken)
            .setRefreshToken(newRefreshToken);

        window.dispatchEvent(
            new CustomEvent("AUTH_TOKEN_REFRESHED", { detail: newToken })
        );

        return `jwt ${newToken}`;
    } catch (error) {
        console.error("Token refresh failed:", error);
        forceLogout("Token refresh failed");
        return null;
    }
};

const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
        if (graphQLErrors) {
            for (let err of graphQLErrors) {
                if (ERROR_MESSAGES.indexOf(err?.message) > -1) {
                    forceLogout(
                        `Logging Out [${err?.path?.[0]}]: ${err?.message}`
                    );
                    return;
                }

                if (err?.message === "Signature has expired") {
                    if (!isRefreshing) {
                        isRefreshing = true;

                        return new Observable((observer) => {
                            getNewToken()
                                .then((newToken) => {
                                    if (!newToken) {
                                        observer.error(
                                            new Error("Failed to refresh token")
                                        );
                                        return;
                                    }

                                    const oldHeaders =
                                        operation.getContext().headers;
                                    operation.setContext({
                                        headers: {
                                            ...oldHeaders,
                                            authorization: newToken,
                                        },
                                    });

                                    forward(operation).subscribe({
                                        next: observer.next.bind(observer),
                                        error: observer.error.bind(observer),
                                        complete:
                                            observer.complete.bind(observer),
                                    });
                                })
                                .catch((error) => {
                                    observer.error(error);
                                })
                                .finally(() => {
                                    isRefreshing = false;
                                    onRefreshComplete();
                                });
                        });
                    } else {
                        return new Observable((observer) => {
                            subscribeTokenRefresh((token) => {
                                const oldHeaders =
                                    operation.getContext().headers;
                                operation.setContext({
                                    headers: {
                                        ...oldHeaders,
                                        authorization: token,
                                    },
                                });

                                forward(operation).subscribe({
                                    next: observer.next.bind(observer),
                                    error: observer.error.bind(observer),
                                    complete: observer.complete.bind(observer),
                                });
                            });
                        });
                    }
                }
            }
        }
        if (networkError) console.error(`[Network error]: ${networkError}`);
    }
);

const retryLink = new RetryLink({
    delay: {
        initial: 2000,
    },
    attempts: {
        max: 10,
        retryIf: (error, _operation) => {
            const statusCode = error?.statusCode;
            if (statusCode > 399 && statusCode < 500) return false;
            return !!error;
        },
    },
});

export const client = new ApolloClient({
    defaultOptions: {
        watchQuery: {
            errorPolicy: "all",
            fetchPolicy: "no-cache",
        },
        query: {
            errorPolicy: "all",
            fetchPolicy: "no-cache",
        },
        mutate: {
            errorPolicy: "all",
            fetchPolicy: "no-cache",
        },
    },
    cache: new InMemoryCache(),
    link: from([
        authMiddleware,
        impersonatedHeaderMiddleware,
        dateTimeHeaderMiddleware,
        errorLink,
        retryLink,
        dynamicLink,
    ]),
});

export const clearApolloCache = () => {
    if (client) {
        console.debug("-- start clearing apollo cache --");
        return client?.clearStore();
    }
};

function forceLogout(errorMessage) {
    if (window?.location?.pathname === "/oauth") {
        return;
    }

    let nextPage = null;
    if (window?.location?.search?.indexOf("next=/courses") === -1) {
        nextPage = window?.location?.pathname ?? null;
        if (
            nextPage == "/" ||
            nextPage == "/login" ||
            nextPage == "/register" ||
            nextPage == "/profile/update" ||
            nextPage == "/oauth"
        ) {
            nextPage = null;
        }
    }

    console.error(errorMessage);
    localStorageWrapper.clearAuthValues().clearImpersonate();
    location.replace(`/login${nextPage ? `?next=${nextPage}` : ""}`);

    clearApolloCache()?.then(() => {
        unsetSupportId();
    });
}
